import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $recipientByIdStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { RecipientInfo, RecipientsDetails, RecipientTransaction } from './widgets';
import { PageContainer, View } from '@widgets';
import { Icon, Text } from '@shared/widgets';
import ArrowIcon from '@assets/icons/arrow.svg';
import ArrowVCFCIcon from '@assets/icons/arrow-vcfc.svg';

export const RecipientPage = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useRouteHook();

  const recipientByIdStore = useUnit($recipientByIdStore.store);

  const { t } = useTranslation('recipient');

  useEffect(() => {
    if (!id) return;
    $recipientByIdStore.events.getRecipient({ id });
    return () => $recipientByIdStore.events.clear();
  }, [id]);

  useEffect(() => {
    if (recipientByIdStore.loading) return;
    if (recipientByIdStore.error !== 'Action is forbidden.') return;
    $recipientByIdStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [recipientByIdStore.loading, recipientByIdStore.error]);

  return (
    <PageContainer
      title={t('title')}
      header={
        <Link className='group flex items-center gap-2' to={RoutePaths.payments.onNavigate()}>
          <Icon
            icon={PaletteUtils.isInsigniaPalette ? <ArrowIcon /> : <ArrowVCFCIcon className='text-black dark:text-white' />}
            className={cn(PaletteUtils.isInsigniaPalette && 'rotate-180')}
          />
          {PaletteUtils.isInsigniaPalette ? (
            <Text weight='semibold' size='2xl'>
              {t('header')}
            </Text>
          ) : (
            <Text className='group-hover:underline' size='sm'>
              {t('return')}
            </Text>
          )}
        </Link>
      }
    >
      <View
        loading={recipientByIdStore.loading}
        error={recipientByIdStore.error}
        onRefresh={() => $recipientByIdStore.events.getRecipient({ id: id! })}
        noData={!recipientByIdStore.data}
      >
        <div className='flex items-stretch gap-6'>
          <div className='flex flex-1 flex-col gap-6'>
            <RecipientInfo recipient={recipientByIdStore.data!} />
            <RecipientTransaction recipient={recipientByIdStore.data!} />
          </div>
          <RecipientsDetails className='basis-[27%]' recipient={recipientByIdStore.data!} />
        </div>
      </View>
    </PageContainer>
  );
};
